import React, { useState } from "react"
import PropTypes from "prop-types"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types"
import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import Link from "components/elements/link"
import { getCentreOffer, getNearbyAreas } from "utils/clinicHeadline"
import {
  clinicKey,
  findAsset,
  adjustTherapyList,
  nearbyAreasFor,
  overridesFor,
} from "utils/clinicOverrides"
import { NetworkSearchIcon, StethoscopeIcon } from "../clinicHero/badgeIcons"
import ClinicCarousel from "./carousel"
import "components/sections/sectionSplitImageCarouselV2/section-split-image-carousel-v2.scss"
import "./clinicWhyChoose.scss"

const ENROLL_PATH = "/welcomevisit/"

// The same sentence on every clinic page, with the centre's name slotted in.
const leadIn = name =>
  `What sets Cortica's ${name} center apart is our integrated, whole-child model, bringing doctors, therapists, and counselors together in one place to deliver coordinated, personalized autism care.`

/**
 * The second paragraph, identical at every centre.
 *
 * This was previously whatever loose copy each Contentful entry happened to
 * carry, so the section read differently on every page. Anything genuinely
 * specific to a centre, room counts, square footage, facilities, belongs in
 * the bullet list below, which stays per-centre.
 */
const STANDARD_BODY =
  "Your child's clinicians share one location and one plan, so what a therapist notices informs their medical care, and what a physician finds shapes their therapy. That means less repeating your story, fewer trips across town, and a plan that keeps pace as your child grows."

/**
 * TEMPORARY, remove once Contentful is updated.
 *
 * The redesign trims each centre's list to the facility features, dropping the
 * therapies already covered by the Services section above. Carlsbad's Contentful
 * entry still holds the old six bullets, so its approved copy is pinned here to
 * preview the design.
 *
 * Keyed by slug on purpose: every other centre keeps its own Contentful content,
 * because these bullets state centre-specific facts (room counts, facilities)
 * that would be wrong anywhere else.
 *
 * To retire this: edit the clinic's Headline field in Contentful to hold only
 * the approved bullets, then delete the matching entry below.
 */
const BULLET_OVERRIDES = {
  carlsbad: [
    "More than 20 rooms for therapies and medical evaluations",
    "Sensorimotor therapies including physical, occupational, and music therapy",
    "Cortica Carlsbad also features a large gym with a rock-climbing wall and access to outdoor space.",
  ],
}

const Chevron = ({ isOpen }) => (
  <svg
    aria-hidden="true"
    className={`ClinicWhyChoose__chevron${
      isOpen ? " ClinicWhyChoose__chevron--open" : ""
    }`}
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      d="m6 9 6 6 6-6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

Chevron.propTypes = { isOpen: PropTypes.bool }

/**
 * Split a leading heading off the editor's copy.
 *
 * Editors naturally write the section heading as the first line of the field.
 * It is lifted out and put in the section's own title element, so the words
 * stay theirs while the styling matches every other section, and so the
 * heading is never shown twice.
 */
const splitHeading = json => {
  const [first, ...rest] = json?.content || []
  if (!first?.nodeType?.startsWith("heading"))
    return { body: json, heading: "" }

  const heading = (first.content || [])
    .map(child => child.value || "")
    .join("")
    .trim()

  return { body: { ...json, content: rest }, heading }
}

/** True when the field holds anything more than Contentful's empty paragraph. */
const hasContent = json =>
  (json?.content || []).some(node =>
    (node.content || []).some(child =>
      child.nodeType === "text" ? child.value?.trim() : true
    )
  )

const isBlank = node =>
  (node.content || []).every(child => !(child.value || "").trim())

/**
 * How the editor's copy is rendered.
 *
 * The shared RichText component is not used here. It wraps its output in a
 * page-width container with its own margins, which inside this narrow column
 * squeezes the text to a sliver. These renderers map Contentful's nodes onto
 * the same classes the generated copy below uses, so a centre reads the same
 * whether its words come from Contentful or from the code.
 */
const BODY_OPTIONS = {
  renderMark: {
    [MARKS.BOLD]: text => <b>{text}</b>,
    [MARKS.ITALIC]: text => <i>{text}</i>,
    [MARKS.UNDERLINE]: text => <u>{text}</u>,
  },
  renderNode: {
    // Contentful leaves an empty paragraph at the end of most fields. Rendering
    // it would open a gap above the Nearby Areas accordion.
    [BLOCKS.PARAGRAPH]: (node, children) =>
      isBlank(node) ? null : (
        <p className="ClinicWhyChoose__lead">{children}</p>
      ),
    // Any heading below the first is a sub-heading, styled like the line that
    // introduces the bullet list.
    [BLOCKS.HEADING_1]: (_, children) => (
      <p className="ClinicWhyChoose__offer-title">{children}</p>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <p className="ClinicWhyChoose__offer-title">{children}</p>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <p className="ClinicWhyChoose__offer-title">{children}</p>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <p className="ClinicWhyChoose__offer-title">{children}</p>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul className="ClinicWhyChoose__list">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ul className="ClinicWhyChoose__list">{children}</ul>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <Link to={node?.data?.uri}>{children}</Link>
    ),
  },
}

// Reuses the split-image-carousel section from the treatment pages, with the
// homepage location carousel on the right instead of markdown slides.
const ClinicWhyChoose = ({ clinic, overrideAssets }) => {
  const [areasOpen, setAreasOpen] = useState(false)

  // Centres with no gallery, or with photos of the wrong building, get an
  // override list of Contentful assets instead.
  const { gallery: overrideGallery, prodGallery } = overridesFor(clinic.slug)

  // Contentful's Why Choose Content replaces the whole body when a centre has
  // filled it in. Until then the section is assembled from Headline as before,
  // so nothing changes for the centres that have not been touched yet.
  const written = hasContent(clinic.whyChooseContent?.json)
    ? splitHeading(clinic.whyChooseContent.json)
    : null

  const offer = getCentreOffer(clinic.headline)
  const nearby = getNearbyAreas(clinic.headline) || nearbyAreasFor(clinic.slug)
  // Contentful bullets list therapies not every centre offers. Fitted here
  // rather than edited across 28 entries. Why Choose Content is left exactly as
  // written, because that field is the editor's own and silently rewriting it
  // would be worse than wrong.
  const bullets = (
    BULLET_OVERRIDES[clinicKey(clinic.slug)] ||
    offer?.items ||
    []
  ).map(item => adjustTherapyList(item, clinic.slug))

  const sourceImages =
    prodGallery ||
    (overrideGallery?.length
      ? overrideGallery.map(title => findAsset(overrideAssets, title))
      : clinic.gallery || [])

  const slides = sourceImages
    .filter(image => image?.fluid)
    .map(image => ({
      fluid: image.fluid,
      id: image.file?.url || image.title,
      title: image.title,
    }))

  // The section always renders. Its heading and lead-in are generated from the
  // centre's name, so it is never empty, and previously it vanished entirely on
  // centres that had neither bullets nor gallery photos.
  const hasCarousel = slides.length > 0

  return (
    <section
      className={`section-split-image-carousel-v2 ClinicWhyChoose${
        hasCarousel ? "" : " ClinicWhyChoose--no-carousel"
      }`}
    >
      <div className="container-v2">
        <div className="section-split-image-carousel-v2--content">
          <h2 className="section-split-image-carousel-v2--title">
            {written?.heading ||
              `Why Choose Our Autism Center in ${clinic.name}?`}
          </h2>

          <div className="section-split-image-carousel-v2--text">
            {written ? (
              documentToReactComponents(written.body, BODY_OPTIONS)
            ) : (
              <>
                {/* Assembled from Headline. The lead-in is built from the
                    centre's name, so this always has copy even where the
                    entry lists no facilities. */}
                <p className="ClinicWhyChoose__lead">{leadIn(clinic.name)}</p>

                <p className="ClinicWhyChoose__lead">{STANDARD_BODY}</p>

                {offer?.title && (
                  <p className="ClinicWhyChoose__offer-title">{offer.title}</p>
                )}

                {bullets.length > 0 && (
                  <ul className="ClinicWhyChoose__list">
                    {bullets.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {nearby && (
            <div className="ClinicWhyChoose__areas">
              <button
                aria-expanded={areasOpen}
                className="ClinicWhyChoose__areas-toggle"
                onClick={() => setAreasOpen(open => !open)}
                type="button"
              >
                <span>{nearby.title}</span>
                <Chevron isOpen={areasOpen} />
              </button>
              {areasOpen && (
                <ul className="ClinicWhyChoose__areas-list">
                  {nearby.areas.map(area => (
                    <li key={area}>{area}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="section-split-image-carousel-v2--cta-buttons under-text-button">
            <p className="section-split-image-carousel-v2--cta-button">
              <ButtonV2
                accessibilityLabel={`Enroll at the ${clinic.name} center`}
                className={getButtonClasses("primary-green", "large")}
                href={ENROLL_PATH}
                showArrow={false}
                text="Enroll Now"
              />
            </p>
          </div>
        </div>

        {hasCarousel && (
          <div className="ClinicWhyChoose__carousel">
            <ClinicCarousel slides={slides} />
            <span
              aria-hidden="true"
              className="ClinicWhyChoose__badge ClinicWhyChoose__badge--top"
            >
              <NetworkSearchIcon />
            </span>
            <span
              aria-hidden="true"
              className="ClinicWhyChoose__badge ClinicWhyChoose__badge--bottom"
            >
              <StethoscopeIcon />
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

ClinicWhyChoose.propTypes = {
  clinic: PropTypes.shape({
    gallery: PropTypes.arrayOf(PropTypes.object),
    headline: PropTypes.shape({ json: PropTypes.object }),
    name: PropTypes.string,
    slug: PropTypes.string,
    whyChooseContent: PropTypes.shape({ json: PropTypes.object }),
  }),
  overrideAssets: PropTypes.array,
}

export default ClinicWhyChoose
